import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import type {
  ApiClient, Heizunguebersicht, Heizungseite, Heizungbauteil, Heizungnetz,
  Heizungknoten, LueftungsbezugErgebnis,
} from '../api';
import { Fachaktionen } from './Fachaktionen';
import { Farbregellegende, farbklasse } from '../Farbregel';
import {
  IconHeizung, IconEbene, IconRaum, IconSuche, IconOk, IconWarnung,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState,
  StatusBadge, formatiereZahl, type Gruppierung, type Spalte,
} from '../muster';

/**
 * Das Gewerk Heizung und Kälte.
 *
 * --- Der Aufbau folgt der Lüftung ---------------------------------------------
 *
 * Links die Gliederung, rechts der gewählte Träger. Bei 2.750 Bauteilen ist eine
 * flache Liste nicht benutzbar, und ein Baum über die ganze Breite mit dem Inhalt
 * darunter zwingt zum Rollen zwischen Auswahl und Anzeige.
 *
 * --- Zwei Gliederungen ---------------------------------------------------------
 *
 *   nach Raum   Geschoss → Raum, getrennt nach Heizung und Kälte.
 *               Für Übergabe und Wartung.
 *
 *   nach Netz   Kreis → Netz. Für die Anlagentechnik.
 *
 * --- Die Farben ----------------------------------------------------------------
 *
 * Hellgrün: aus dem Modell, geht bei einem Rückschreiben dorthin zurück.
 * Hellgelb: nur in der Datenbank — aus Datenblatt, Rechnung oder von Hand.
 *
 * --- TX-STANDARD S1 (TH-2b) -----------------------------------------------------
 *
 * PageHeader mit dem Bestand als Stand und der ExportLeiste mit den Unterlagen
 * (ZIP) des Gewerks (S1.9 Nr. 3: die Einstiegsseite). Die Bauteile stehen in der
 * DataTable mit einer Gruppenzeile je Bauteilart (TX3 `gruppierung`, Farbstreifen
 * über `markierung`, „n prüfen“ als StatusBadge); ein Klick auf eine Zeile öffnet
 * die Datenpflege des Bauteils unter der Tabelle. Lade-, Leer- und
 * Fehlerzustände aus der Musterbibliothek.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

interface Auswahl {
  art: 'raum' | 'netz' | 'bauteilart';
  name: string;
  filter: Bauteilfilter;
}

interface Bauteilfilter {
  bauteilart?: string;
  raum?: string;
  geschoss?: string;
  kreis?: string;
  netz?: string;
}

export function HeizungKaelte() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [uebersicht, setUebersicht] = useState<Heizunguebersicht | null>(null);
  const [nach, setNach] = useState<'raum' | 'netz'>('raum');
  const [kreisfilter, setKreisfilter] = useState('');
  const [baum, setBaum] = useState<Heizungknoten[] | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Auswahl | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    if (!projekt) return;

    try {
      const [u, b] = await Promise.all([
        api.heizungUebersicht(projekt.id),
        api.heizungBaum(projekt.id, nach, kreisfilter || undefined),
      ]);

      setUebersicht(u);
      setBaum(b.baum);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [api, projekt?.id, nach, kreisfilter]);

  // Die Suche filtert den Baum im Browser. Er trägt nur Struktur und Zahlen —
  // wenige Kilobyte — und eine Abfrage je Tastendruck wäre verschwendet.
  const gefiltert = useMemo(() => {
    if (!baum) return null;
    if (!suche.trim()) return baum;

    const treffer = suche.trim().toLowerCase();

    const passt = (k: Heizungknoten): boolean =>
      k.name.toLowerCase().includes(treffer)
      || (k.zusatz ?? '').toLowerCase().includes(treffer)
      || (k.kinder ?? []).some(passt);

    return baum
      .map((e) => ({
        ...e,
        kinder: (e.kinder ?? []).filter(passt),
      }))
      .filter((e) => e.kinder.length > 0 || passt(e));
  }, [baum, suche]);

  const kopf = (
    <PageHeader
      titel="Heizung und Kälte"
      beschreibung={'Bauteile der Heizung, Kälte und Rückkühlung aus dem Modell – nach Raum oder Netz, mit '
        + 'Datenpflege je Bauteil. Rechnungen und Nachweise stehen auf den Unterseiten.'}
      meta={uebersicht ? `${formatiereZahl(uebersicht.gesamt, 0)} Bauteile` : undefined}
      aktionen={projekt ? (
        <ExportLeiste ziele={[
          { art: 'zip', laden: () => api.unterlagenZip(projekt.id, 'heizung') },
        ]} />
      ) : undefined}
    />
  );

  if (!projekt) return <>{kopf}<LoadingState text="Projekt wird gelesen …" /></>;
  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Heizung und Kälte konnten nicht geladen werden." meldung={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      </>
    );
  }

  const leer = !uebersicht || uebersicht.gesamt === 0;

  return (
    <>
      {kopf}

      <Fachaktionen
        api={api}
        rolleKey="heizung_kaelte"
        leseArt={50}
        schreibArt={51}
        leseText="Aus Modell lesen"
        schreibText="Ins Modell schreiben"
        zusatzArten={[{
          art: 52,
          text: 'Rohrnetz lesen',
          titel: 'Rohre, Formstücke und ihre Anschlüsse lesen — rund 40.000 '
               + 'Elemente, etwa zehn bis fünfzehn Minuten. Die Bauteile aus '
               + '„Aus Modell lesen“ bleiben unberührt.',
        }]}
      >
        <Link to="../heizung-datenpflege" className="knopf">
          Datenpflege
        </Link>
        <Link to="../heizung-berechnung" className="knopf"
              title="Ströme, Druckverlust, Förderhöhe je Auslegungsgruppe und
                     die Voreinstellung je Verbraucher">
          Rohrnetzberechnung
        </Link>
        <Link to="../geraete" className="knopf">
          Geräte
        </Link>

        <LueftungsbezugAktion api={api} projektId={projekt.id} />

        <Produktimport api={api} projektId={projekt.id} neuLaden={laden} />
      </Fachaktionen>

      {!uebersicht ? (
        <LoadingState text="Bestand wird gelesen …" zeilen={6} spalten={3} />
      ) : leer ? (
        <EmptyState
          icon={<IconHeizung />}
          titel="Noch kein Heizungsbestand."
          erklaerung={'Er entsteht aus einem Auftrag mit Heizungsaufnahme – oben „Aus Modell lesen“. Dafür muss '
            + 'im Profil unter „Leseläufe“ der Punkt „Heizung und Kälte lesen“ gesetzt und die Fassung '
            + 'freigegeben sein.'}
        />
      ) : (
        <>
          <Kennzahlen uebersicht={uebersicht!} />

          <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
            {/* Links die Gliederung, rechts der gewählte Träger. */}
            <div className="baum">
              <div style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  <button
                    className={nach === 'raum' ? 'betont' : undefined}
                    onClick={() => { setNach('raum'); setOffen(new Set()); }}
                    style={{ flex: 1, padding: '3px 6px', fontSize: 12 }}
                  >
                    Räume
                  </button>
                  <button
                    className={nach === 'netz' ? 'betont' : undefined}
                    onClick={() => { setNach('netz'); setOffen(new Set()); }}
                    style={{ flex: 1, padding: '3px 6px', fontSize: 12 }}
                  >
                    Netze
                  </button>
                </div>

                <select
                  value={kreisfilter}
                  onChange={(e) => setKreisfilter(e.target.value)}
                  style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
                >
                  <option value="">alle Kreise</option>
                  <option value="Heizung">nur Heizung</option>
                  <option value="Kälte">nur Kälte</option>
                  <option value="Rueckkuehlung">nur Rückkühlung</option>
                </select>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconSuche size={14} />
                  <input
                    value={suche}
                    onChange={(e) => setSuche(e.target.value)}
                    placeholder={nach === 'raum' ? 'Raum oder Geschoss' : 'Netz'}
                    style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
                  />
                </label>
              </div>

              {(gefiltert ?? []).map((e) => (
                <div key={e.name}>
                  <button
                    className="baum-ebene"
                    onClick={() => setOffen((s) => {
                      const neu = new Set(s);
                      if (neu.has(e.name)) neu.delete(e.name); else neu.add(e.name);
                      return neu;
                    })}
                  >
                    <IconEbene size={14} />
                    <span>{e.name}</span>
                    <span className="schwach">{e.anzahl}</span>
                  </button>

                  {offen.has(e.name) && (e.kinder ?? []).map((k) => (
                    <button
                      key={k.name}
                      className={`baum-zeile ${
                        gewaehlt?.name === `${e.name}/${k.name}` ? 'aktiv' : ''}`}
                      onClick={() => setGewaehlt({
                        art: nach === 'raum' ? 'raum' : 'netz',
                        name: `${e.name}/${k.name}`,
                        filter: nach === 'raum'
                          ? {
                              geschoss: e.name === 'ohne Geschoss' ? undefined : e.name,
                              raum: k.name === 'ohne Raum' ? undefined : k.name,
                            }
                          : {
                              kreis: e.name === 'ohne Kreis' ? undefined : e.name,
                              netz: k.name === 'ohne Netz' ? undefined : k.name,
                            },
                      })}
                      title={k.zusatz ? `${k.name} — ${k.zusatz}` : k.name}
                    >
                      {nach === 'raum'
                        ? <IconRaum size={13} />
                        : <IconHeizung size={13} />}

                      {/* Nummer und Beschreibung nebeneinander. "E1-042" allein
                          sagt niemandem etwas; wer Daten pflegt, sucht die
                          Technikzentrale. */}
                      <span style={{ minWidth: 0, overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {k.name}
                        {k.zusatz && (
                          <span className="schwach" style={{ marginLeft: 5 }}>
                            {k.zusatz}
                          </span>
                        )}
                      </span>

                      {/* Die Aufteilung nach Kreis — der Kern der raumweisen
                          Ansicht. Nur was vorhanden ist. */}
                      {nach === 'raum' && (
                        <span style={{ display: 'flex', gap: 3, marginLeft: 'auto',
                                       fontSize: 10 }}>
                          {(k.heizung ?? 0) > 0 && (
                            <span style={{ color: 'var(--warnung)' }}
                                  title="Heizung">{k.heizung}H</span>
                          )}
                          {(k.kaelte ?? 0) > 0 && (
                            <span style={{ color: 'var(--akzent)' }}
                                  title="Kälte">{k.kaelte}K</span>
                          )}
                          {(k.rueckkuehlung ?? 0) > 0 && (
                            <span className="sehr-schwach"
                                  title="Rückkühlung">{k.rueckkuehlung}R</span>
                          )}
                        </span>
                      )}

                      {nach === 'netz' && (
                        <span className="schwach">{k.anzahl}</span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {/* Rechts der Inhalt */}
            <div style={{ flex: 1, padding: '14px 16px', minWidth: 0,
                          maxHeight: '70vh', overflowY: 'auto' }}>
              {!gewaehlt ? (
                <EmptyState klein titel={`Links einen ${nach === 'raum' ? 'Raum' : 'Netzstrang'} wählen.`} />
              ) : (
                <Inhalt
                  api={api}
                  projektId={projekt.id}
                  auswahl={gewaehlt}
                  kreisfilter={kreisfilter}
                />
              )}
            </div>
          </div>

          <Netzliste api={api} projektId={projekt.id} />
        </>
      )}
    </>
  );
}

/**
 * Erst die Lagezuordnung zeigen, danach bewusst übernehmen. Die Prüfung ist
 * wichtig: Eine geometrische Nähe ist ein begründeter Vorschlag, keine in
 * Revit modellierte Verbindung.
 */
/** Ein Lüftungsgerät mit seinen Registeranschlüssen (aus der Probe des Lüftungsbezugs). */
interface Lueftungsgeraet {
  plantId: string;
  kennzeichnung: string;
  standort: string;
  geraetetitel: string;
  heizleistungW: number | null;
  heizMassenstromKgH: number | null;
  kuehlleistungW: number | null;
  kuehlMassenstromKgH: number | null;
  anschluesse: number;
  pruefen: boolean;
}

/** W als kW für die Anzeige (gespeichert wird W). */
const kw = (w: number | null) => (w === null ? null : w / 1000);

const GERAETESPALTEN: Spalte<Lueftungsgeraet>[] = [
  { schluessel: 'kennzeichnung', titel: 'Kennzeichnung', immerSichtbar: true, wert: (g) => g.kennzeichnung,
    inhalt: (g) => <strong>{g.kennzeichnung}</strong> },
  { schluessel: 'standort', titel: 'Standort', wert: (g) => g.standort },
  { schluessel: 'titel', titel: 'Gerätetitel', wert: (g) => g.geraetetitel },
  { schluessel: 'heiz', titel: 'Heizregister', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (g) => kw(g.heizleistungW) },
  { schluessel: 'heizMasse', titel: 'Massenstrom H', einheit: 'kg/h', art: 'zahl', nachkomma: 0,
    wert: (g) => g.heizMassenstromKgH },
  { schluessel: 'kuehl', titel: 'Kühlregister', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (g) => kw(g.kuehlleistungW) },
  { schluessel: 'kuehlMasse', titel: 'Massenstrom K', einheit: 'kg/h', art: 'zahl', nachkomma: 0,
    wert: (g) => g.kuehlMassenstromKgH },
  { schluessel: 'pruefung', titel: 'Prüfung', art: 'status', wert: (g) => (g.pruefen ? 'prüfen' : 'vorhanden'),
    inhalt: (g) => <StatusBadge klein zustand={g.pruefen ? 'warnung' : 'gut'} text={g.pruefen ? 'prüfen' : 'vorhanden'} /> },
];

function LueftungsbezugAktion({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [ergebnis, setErgebnis] = useState<LueftungsbezugErgebnis | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const geraete = useMemo(() => {
    if (!ergebnis) return [];
    const nachAnlage = new Map<string, Lueftungsgeraet>();

    for (const z of ergebnis.zuordnungen) {
      let g = nachAnlage.get(z.plantId);
      if (!g) {
        g = {
          plantId: z.plantId,
          kennzeichnung: z.kennzeichnung || z.anlage,
          standort: z.standort || '—',
          geraetetitel: z.geraetetitel || z.anlage,
          heizleistungW: null,
          heizMassenstromKgH: null,
          kuehlleistungW: null,
          kuehlMassenstromKgH: null,
          anschluesse: 0,
          pruefen: false,
        };
        nachAnlage.set(z.plantId, g);
      }
      g.anschluesse += 1;
      g.pruefen ||= !z.amNetz || z.hinweis?.includes('WIDERSPRUCH:') === true;
      if (z.modus === 'heating') {
        g.heizleistungW ??= z.leistungW;
        g.heizMassenstromKgH ??= z.massenstromKgH;
      } else if (z.modus === 'cooling') {
        g.kuehlleistungW ??= z.leistungW;
        g.kuehlMassenstromKgH ??= z.massenstromKgH;
      }
    }
    return [...nachAnlage.values()].sort((a, b) =>
      a.kennzeichnung.localeCompare(b.kennzeichnung, 'de', { numeric: true }));
  }, [ergebnis]);

  const ausfuehren = async (probe: boolean) => {
    setLaeuft(true);
    setFehler(null);
    try {
      setErgebnis(await api.heizungLueftungsbezug(projektId, probe));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                  flexWrap: 'wrap', flex: '1 1 100%' }}>
      <button className="knopf" type="button" disabled={laeuft}
              onClick={() => ausfuehren(true)}
              title="Anschlusspunkte und Registerdaten prüfen, ohne zu schreiben">
        {laeuft ? 'Zuordnung läuft …' : 'Lüftungsbezüge prüfen'}
      </button>

      {ergebnis && (
        <span className={ergebnis.offen.length ? 'hinweis warnung' : 'hinweis'}
              style={{ margin: 0, padding: '5px 9px' }}>
          {ergebnis.zugeordnet}/{ergebnis.anschlusspunkte} Anschlüsse →{' '}
          {ergebnis.anlagen} Anlagen, {ergebnis.offen.length} offen
          {!ergebnis.probe && ' · gespeichert'}
        </span>
      )}
      {fehler && <span className="hinweis fehler">{fehler}</span>}

      {ergebnis?.probe && (
        <div style={{ flexBasis: '100%', minWidth: 0, marginTop: 6 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <strong>{geraete.length} Lüftungsgeräte zur Übernahme</strong>
            <span className="schwach"> · Leistung am Anschlusspunkt; Massenstrom aus Leistung, Wärmekapazität und Vor-/Rücklauftemperatur</span>
          </div>
          <DataTable
            name="Lüftungsgeräte zur Übernahme"
            spalten={GERAETESPALTEN}
            zeilen={geraete}
            schluessel={(g) => g.plantId}
            dicht
            hoehe={430}
            leer={<EmptyState klein titel="Kein Lüftungsgerät mit Anschluss gefunden." />}
          />

          {ergebnis.offen.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--warnung)' }}>
                {ergebnis.offen.length} offene Anschlusspunkte anzeigen
              </summary>
              <ul style={{ margin: '7px 0 0' }}>
                {ergebnis.offen.map((o) => (
                  <li key={o.bauteilId}>Revit-Element {o.revitElementId ?? '—'}: {o.grund}</li>
                ))}
              </ul>
            </details>
          )}

          <button className="knopf primaer" type="button" disabled={laeuft}
                  style={{ marginTop: 10 }} onClick={() => ausfuehren(false)}>
            {ergebnis.zugeordnet} geprüfte Bezüge übernehmen
          </button>
        </div>
      )}
    </div>
  );
}

// --- Die Kennzahlen ----------------------------------------------------------

function Kennzahlen({ uebersicht }: { uebersicht: Heizunguebersicht }) {
  const gefuehrt = uebersicht.arten.filter((a) => a.wirdGefuehrt);

  return (
    <div className="karte">
      <h2 className="karte-titel" style={{ marginTop: 0 }}>
        <IconHeizung />Heizung und Kälte
        <span className="schwach">{uebersicht.gesamt} Bauteile</span>
      </h2>

      <KpiReihe>
        <KpiCard titel="Bauteile" wert={uebersicht.gesamt} nachkomma={0} />
        <KpiCard titel="Raum aus der Lage" wert={gefuehrt.reduce((n, a) => n + (a.raumAusLage ?? 0), 0)}
                 nachkomma={0} vergleich="errechnet, keine Angabe" />
        <KpiCard titel="ohne Raum" wert={gefuehrt.reduce((n, a) => n + (a.ohneRaum ?? 0), 0)} nachkomma={0} />
        <KpiCard titel="Netze" wert={uebersicht.netze.reduce((n, x) => n + x.anzahl, 0)} nachkomma={0} />
      </KpiReihe>

      {uebersicht.entfernt > 0 && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          <IconWarnung size={13} />
          <div style={{ fontSize: 12 }}>
            <strong>{uebersicht.entfernt} Bauteil(e)</strong> waren beim letzten
            Lesen nicht mehr im Modell und sind als entfernt vermerkt. Gelöscht wird
            nichts — kommen sie wieder, gilt der Vermerk nicht mehr.
          </div>
        </div>
      )}
    </div>
  );
}

// --- Der Inhalt rechts -------------------------------------------------------

function Inhalt({
  api, projektId, auswahl, kreisfilter,
}: {
  api: ApiClient;
  projektId: string;
  auswahl: Auswahl;
  kreisfilter: string;
}) {
  const [seite, setSeite] = useState<Heizungseite | null>(null);
  const [offenesBauteil, setOffenesBauteil] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setOffenesBauteil(null);

    api.heizungBauteile(projektId, {
      ...auswahl.filter,
      kreis: auswahl.filter.kreis ?? (kreisfilter || undefined),
      proSeite: 300,
    })
      .then(setSeite)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, auswahl, kreisfilter]);

  if (fehler) return <ErrorState klein titel="Die Bauteile konnten nicht geladen werden." meldung={fehler} />;
  if (!seite) return <LoadingState text="Bauteile werden gelesen …" zeilen={6} spalten={5} />;

  // Nach Bauteilart gruppiert (TX3 `gruppierung`). Die Reihenfolge bleibt, wie
  // sie kam - der Server sortiert bereits nach der Reihenfolge im Katalog, und
  // eine zweite Sortierung hier würde ihr widersprechen (`ordnung: 'eingang'`).
  // Ohne die Gruppenzeile laufen achtzig Armaturen und drei Pumpen ineinander.
  const arten = [...new Set(seite.bauteile.map((b) => b.bauteilart))];
  const gruppierung: Gruppierung<Bauteil> = {
    nach: (b) => b.bauteilart,
    titel: (art) => seite.arten?.[art] ?? art,
    markierung: (art) => (arten.indexOf(art) % 8) + 1,
    ordnung: 'eingang',
    // Wie weit die Art versorgt ist - die Zahl gehört an den Gruppenkopf, nicht an jede Zeile.
    zusatz: (_, zeilen) => {
      const pruefen = zeilen.filter((b) => b.datenstand === 'pruefen').length;
      const offen = zeilen.filter((b) => !b.datenstand || b.datenstand === 'offen').length;
      return (
        <>
          {pruefen > 0 && <StatusBadge klein zustand="warnung" text={`${pruefen} prüfen`} />}
          {offen > 0 && <StatusBadge klein zustand="neutral" text={`${offen} ohne Daten`} />}
        </>
      );
    },
  };

  const offenes = seite.bauteile.find((b) => b.id === offenesBauteil) ?? null;

  return (
    <>
      <h2 className="karte-titel" style={{ marginTop: 0 }}>
        {auswahl.name.split('/').pop()}
        <span className="schwach">{formatiereZahl(seite.gesamt, 0)} Bauteile</span>
      </h2>

      {seite.gesamt > seite.proSeite && (
        <p className="sehr-schwach" style={{ fontSize: 11 }}>
          Es werden {formatiereZahl(seite.proSeite, 0)} von {formatiereZahl(seite.gesamt, 0)} gezeigt.
        </p>
      )}

      <DataTable
        name="Bauteile"
        spalten={BAUTEILSPALTEN}
        zeilen={seite.bauteile}
        schluessel={(b) => b.id}
        gruppierung={gruppierung}
        dicht
        beiZeileKlick={(b) => setOffenesBauteil(offenesBauteil === b.id ? null : b.id)}
        aktiveZeile={offenesBauteil ?? undefined}
        zeilenKlasse={(b) => (b.entferntAm ? 'schwach' : undefined)}
        leer={<EmptyState klein titel="Keine Bauteile in dieser Auswahl." />}
      />

      {/* Die Datenpflege des gewählten Bauteils unter der Tabelle: ein Klick auf
          die Zeile öffnet sie, ein zweiter schließt sie. */}
      {offenes && (
        <Bauteildetail key={offenes.id} b={offenes} api={api} schliessen={() => setOffenesBauteil(null)} />
      )}
    </>
  );
}

type Bauteil = Heizungseite['bauteile'][number];

/** Der Name eines Bauteils mit seinem Datenzustand. */
function Bauteilname({ b }: { b: Bauteil }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>{b.bezeichnung ?? b.familie ?? '—'}</span>
      {b.entferntAm && <StatusBadge klein zustand="warnung" text="entfernt" />}
      {/* "prüfen" wird hervorgehoben: Der Kennwert lag außerhalb der Baugrößen,
          der Wert ist hochgerechnet. */}
      {b.datenstand === 'pruefen' && (
        <StatusBadge klein zustand="warnung" text="prüfen" titel={b.datenstandGrund ?? 'Bitte prüfen'} />
      )}
      {b.datenstand === 'zugewiesen' && <StatusBadge klein zustand="gut" text="Daten" />}
      {(!b.datenstand || b.datenstand === 'offen') && (
        <span className="sehr-schwach" style={{ fontSize: 9 }}>offen</span>
      )}
      {b.raumHerkunft === 'lage' && (
        <span className="sehr-schwach" style={{ fontSize: 9 }}
              title="Raum aus der Lage errechnet — eine Vermutung">Lage</span>
      )}
    </span>
  );
}

const BAUTEILSPALTEN: Spalte<Bauteil>[] = [
  { schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true,
    wert: (b) => b.bezeichnung ?? b.familie ?? '', inhalt: (b) => <Bauteilname b={b} /> },
  { schluessel: 'typ', titel: 'Typ', wert: (b) => b.typ ?? '' },
  // Die genaue Ebene, wo sie von der Etage abweicht. Im Baum steht G01, hier
  // steht G01_UKRD - beides wird gebraucht.
  { schluessel: 'ebene', titel: 'Ebene',
    wert: (b) => (b.geschoss && b.geschoss !== b.geschossgruppe ? b.geschoss : '') },
  { schluessel: 'kreis', titel: 'Kreis', wert: (b) => b.kreis ?? '' },
  { schluessel: 'netz', titel: 'Netz', wert: (b) => b.systemabkuerzung ?? '' },
];

// --- Die Datenpflege eines Bauteils -------------------------------------------

function Bauteildetail({
  b, api, schliessen,
}: {
  b: Bauteil;
  api: ApiClient;
  schliessen: () => void;
}) {
  const [detail, setDetail] = useState<Heizungbauteil | null>(null);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (detail) return;

    api.heizungBauteil(b.id).then(setDetail).catch(() => setDetail(null));
  }, [api, b.id, detail]);

  const speichern = async () => {
    if (Object.keys(entwurf).length === 0) return;

    setLaeuft(true);
    try {
      const a = await api.heizungWerteSpeichern(b.id, entwurf);
      setMeldung(a?.message ?? 'Gespeichert.');
      setEntwurf({});
      setDetail(await api.heizungBauteil(b.id));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const gruppen = [...new Set((detail?.werte ?? []).map((w) => w.gruppe ?? 'Sonstiges'))];

  return (
    <div className="karte" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{b.bezeichnung ?? b.familie ?? '—'}</h3>
        <span className="schwach">{b.typ ?? ''}</span>
        <button type="button" className="m-knopf" style={{ marginLeft: 'auto' }} onClick={schliessen}>
          Schließen
        </button>
      </div>
            {!detail ? (
              <LoadingState text="Felder werden gelesen …" zeilen={4} spalten={3} />
            ) : (
              <div style={{ padding: '8px 4px' }}>
                {meldung && (
                  <div className="hinweis" style={{ marginBottom: 10 }}>
                    <IconOk size={13} /> {meldung}
                  </div>
                )}

                {detail.entferntAm === null && b.datenstandGrund && (
                  <div className="hinweis" style={{ marginBottom: 10 }}>
                    <IconWarnung size={13} />
                    <div style={{ fontSize: 12 }}>{b.datenstandGrund}</div>
                  </div>
                )}

                {/* Datenblatt und Verweise zuerst — sie sind das Ziel der
                    Pflege, nicht ein Anhang. */}
                {(b.datenblattUrl || b.herstellerUrl) && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12,
                                flexWrap: 'wrap' }}>
                    {b.datenblattUrl && (
                      <a href={b.datenblattUrl} target="_blank" rel="noreferrer"
                         style={{ fontSize: 12 }}>
                        Datenblatt öffnen
                      </a>
                    )}
                    {b.herstellerUrl && (
                      <a href={b.herstellerUrl} target="_blank" rel="noreferrer"
                         style={{ fontSize: 12 }}>
                        Herstellerseite
                      </a>
                    )}
                    <span className="sehr-schwach" style={{ fontSize: 10 }}>
                      Der Datenblattverweis ist ohne Anmeldung erreichbar und kann
                      ins Modell geschrieben werden.
                    </span>
                  </div>
                )}

                {gruppen.map((g) => (
                  <div key={g} style={{ marginBottom: 14 }}>
                    <div className="feldname" style={{ fontSize: 12 }}>{g}</div>

                    <div className="feldgitter">
                      {detail.werte.filter((w) => (w.gruppe ?? 'Sonstiges') === g)
                        .map((w) => (
                          <div
                            key={w.feldschluessel}
                            /* Hellgrün: Das Feld geht beim Rückschreiben ins
                               Modell zurück. Hellgelb: Der Wert bleibt in der
                               Datenbank.

                               Hier stand bis PB53 `w.ausModell` — und das war
                               `RevitParameter != null`, also die MÖGLICHKEIT
                               anstelle der FESTLEGUNG, obwohl dieselbe Antwort
                               in derselben Auskunft schon danebenlag. 13 der
                               64 Heizungsfelder mit Parameter sind gesperrt
                               (Revit-eigene Parameter und seit 0732 die
                               bauteilnummer auf dem Identitätsanker); sie
                               erschienen hier hellgrün. */
                            className={`${farbklasse(w.gehtInsModell)} feld`}
                            style={{ padding: '4px 7px' }}
                          >
                            <div className="feldname" style={{ fontSize: 11 }}>
                              {w.bezeichnung}
                              {w.einheit && ` [${w.einheit}]`}
                            </div>

                            {w.art === 'auswahl' && w.auswahl ? (
                              <select
                                value={entwurf[w.feldschluessel] ?? w.wert ?? ''}
                                onChange={(e) => setEntwurf({
                                  ...entwurf, [w.feldschluessel]: e.target.value,
                                })}
                                style={{ fontSize: 12 }}
                              >
                                <option value="">—</option>
                                {w.auswahl.split('|').map((x) => (
                                  <option key={x} value={x}>{x}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                value={entwurf[w.feldschluessel] ?? w.wert ?? ''}
                                onChange={(e) => setEntwurf({
                                  ...entwurf, [w.feldschluessel]: e.target.value,
                                })}
                                style={{ fontSize: 12 }}
                              />
                            )}

                            <div className="sehr-schwach" style={{ fontSize: 9 }}>
                              {/* Der Parametername sagt, WOHER der Wert
                                  kommt — nicht, ob er zurückgeht. An einem
                                  gesperrten Feld muss der Zusatz dabeistehen,
                                  sonst liest sich der Name wie eine Zusage. */}
                              {w.gehtInsModell
                                ? <code>{w.revitParameter}</code>
                                : w.revitParameter
                                  ? <span><code>{w.revitParameter}</code> — wird nur gelesen</span>
                                  : 'nur Datenbank'}
                              {w.herkunft && ` · ${w.herkunft}`}
                              {w.weichtAb && ` · Modell: ${w.wertImModell}`}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    className={Object.keys(entwurf).length > 0 ? 'betont' : undefined}
                    disabled={laeuft || Object.keys(entwurf).length === 0}
                    onClick={speichern}
                  >
                    {laeuft ? 'wird gespeichert …'
                      : `Speichern (${Object.keys(entwurf).length})`}
                  </button>

                  <Legende />
                </div>
              </div>
            )}
    </div>
  );
}

/**
 * Die Farbregel steht seit V-P1 in `Farbregel.tsx` und nicht mehr hier: Die
 * frueheren Worte "aus dem Modell" / "nur Datenbank" beschrieben dieselbe
 * Regel wie die Elektro- und die Sanitaerseite, nur anders - und "aus dem
 * Modell" liest sich als Aussage ueber den WERT, waehrend die Farbe dem FELD
 * gilt.
 */
function Legende() {
  return <Farbregellegende />;
}

// --- Die Netze ---------------------------------------------------------------

const NETZSPALTEN: Spalte<Heizungnetz>[] = [
  { schluessel: 'system', titel: 'System', immerSichtbar: true, wert: (n) => n.systemname },
  { schluessel: 'kuerzel', titel: 'Kürzel', wert: (n) => n.abkuerzung ?? '' },
  { schluessel: 'kreis', titel: 'Kreis', wert: (n) => n.kreis ?? '' },
  { schluessel: 'richtung', titel: 'Richtung',
    wert: (n) => (n.istVorlauf === true ? 'Vorlauf' : n.istVorlauf === false ? 'Rücklauf' : '') },
  { schluessel: 'elemente', titel: 'Elemente', art: 'zahl', nachkomma: 0, wert: (n) => n.elemente },
];

function Netzliste({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [netze, setNetze] = useState<Heizungnetz[] | null>(null);
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    if (!offen || netze) return;

    api.heizungNetze(projektId).then(setNetze).catch(() => setNetze([]));
  }, [offen, api, projektId, netze]);

  return (
    <div className="karte">
      <h2 className="karte-titel" style={{ marginTop: 0 }}>
        Rohrnetze
        <button type="button" className="m-knopf" aria-expanded={offen} onClick={() => setOffen(!offen)}
                style={{ marginLeft: 'auto' }}>
          {offen ? 'ausblenden' : 'anzeigen'}
        </button>
      </h2>

      {offen && (
        <DataTable
          name="Rohrnetze"
          spalten={NETZSPALTEN}
          zeilen={netze ?? []}
          schluessel={(n) => n.id}
          laedt={netze === null}
          dicht
          hoehe={340}
          leer={<EmptyState klein titel="Keine Rohrnetze gelesen." />}
        />
      )}
    </div>
  );
}

// --- Der Produktimport -------------------------------------------------------

/**
 * Herstellerdaten als JSON einlesen.
 *
 * Getrennt vom Lüftungsdialog, wie besprochen: Die Bauteilarten sind zu
 * verschieden. Ein Wärmetauscher hat Primär- und Sekundärkreis, ein Lüftungsgerät
 * nicht — und die Felder, auf die abgebildet wird, stammen aus einem anderen
 * Katalog.
 */
function Produktimport({
  api, projektId, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  neuLaden: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [befund, setBefund] = useState<string[] | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const einlesen = async (datei: File, nurPruefen: boolean) => {
    setLaeuft(true);
    setMeldung(null);
    setBefund(null);
    try {
      const a = await api.heizungProduktimport(projektId, datei, nurPruefen);

      setMeldung(a.message);
      setBefund(a.hinweise ?? null);

      if (!nurPruefen) neuLaden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <button onClick={() => setOffen(!offen)}>
        Produktdaten einlesen
      </button>

      {offen && (
        <div className="karte" style={{ marginTop: 10, width: '100%' }}>
          <div className="feldname">Herstellerdaten als JSON</div>

          <p className="schwach" style={{ fontSize: 12, marginTop: 4, maxWidth: 700 }}>
            Füllt die Felder, die im Modell nicht stehen — Vor- und
            Rücklauftemperatur, elektrische Leistung, Schalldruck, Betriebsgewicht.
            Zugeordnet wird über <code>UniqueId</code>, <code>Artikelnummer</code>{' '}
            oder <code>Typ</code>.
          </p>

          <p className="sehr-schwach" style={{ fontSize: 11, maxWidth: 700 }}>
            Eingelesene Werte gelten als <em>Datenblatt</em> und werden vom nächsten
            Auslesen nicht überschrieben. Erst prüfen, dann übernehmen — die Prüfung
            ändert nichts.
          </p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                        marginTop: 10, flexWrap: 'wrap' }}>
            <input
              type="file"
              accept="application/json,.json"
              disabled={laeuft}
              onChange={(e) => {
                const d = e.target.files?.[0];
                if (d) einlesen(d, true);
                e.target.value = '';
              }}
              style={{ fontSize: 13 }}
            />
            <span className="sehr-schwach" style={{ fontSize: 11 }}>
              Datei wählen — sie wird zuerst nur geprüft
            </span>
          </div>

          {meldung && (
            <div className="hinweis" style={{ marginTop: 10 }}>
              <IconOk size={13} /> {meldung}
            </div>
          )}

          {befund && befund.length > 0 && (
            <ul style={{ fontSize: 12, marginTop: 8, paddingLeft: 18 }}>
              {befund.slice(0, 20).map((h, i) => <li key={i}>{h}</li>)}
              {befund.length > 20 && (
                <li className="sehr-schwach">… und {befund.length - 20} weitere</li>
              )}
            </ul>
          )}

          {befund && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                          marginTop: 10 }}>
              <input
                type="file"
                accept="application/json,.json"
                disabled={laeuft}
                onChange={(e) => {
                  const d = e.target.files?.[0];
                  if (d) einlesen(d, false);
                  e.target.value = '';
                }}
                style={{ fontSize: 13 }}
              />
              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                Dieselbe Datei erneut wählen, um sie zu übernehmen
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
